import { DEFAULT_LINT_OPTIONS, type LintOptions } from '@jsonfmt/core';

import { DEFAULT_LOCALE } from '@/locales';
import { DEFAULT_THEME_PREFERENCE, STORAGE_KEYS } from '@/services/types';

/**
 * THE TUNABLE VALUES THE COMPOSITION ROOT INJECTS — docs/ARCHITECTURE.md §3.
 *
 * ═══ WHAT THIS FILE CLAIMED, AND WHY THE CLAIM WAS UNKEEPABLE ═══
 *
 * It used to open with "EVERY tunable number, default and storage key of the application, in
 * one file … Nothing below may be re-declared anywhere else — if a module needs a number, it
 * imports it from here." That sentence could not be true, and the audit of 2026-09-14
 * measured how untrue it was: twelve of its twenty exports had no importer anywhere in the
 * repository, and the values they named were re-declared as literals in the layers that
 * actually needed them.
 *
 * The cause is structural, not sloppiness. Gate 2 denies `@/app/*` to `services/`,
 * `features/` and `ui/` (ADR-0003), so three of the four layers CANNOT import this file. A
 * single home that three quarters of the application is forbidden to visit is not a single
 * home; it is a decoy, and a decoy is worse than nothing — a developer who changed
 * `STORAGE_KEYS.theme` here changed nothing at all, and no gate objected.
 *
 * ═══ WHAT IS TRUE INSTEAD ═══
 *
 * Two rules, and every line below obeys one of them.
 *
 *   1. A value only this layer needs is DECLARED here. The size ladder and the debounce
 *      ladder are of this kind: they are arguments this root passes to `createServices` and
 *      `DocumentStore`, and no other layer has any business knowing them.
 *
 *   2. A value that must agree ACROSS a gate is declared in the lowest module every party can
 *      import, and NAMED here. `HARD_LIMIT_BYTES = DEFAULT_LINT_OPTIONS.maxBytes` was already
 *      the worked example of this; `STORAGE_KEYS`, `DEFAULT_THEME_PREFERENCE` and
 *      `DEFAULT_LOCALE` now follow it. The re-export is not decoration: it keeps this file
 *      the readable index of the application's tunables while leaving exactly one
 *      declaration, in a place the other layers can reach.
 *
 * Values that belong to one layer and are used only there stay there and are NOT named here:
 * `TOAST_CEILING` in `ui/notifications/toastHub.ts` is the stack depth of a presentation
 * component, and mirroring it into a `TOAST_LIMIT` nobody imports is how the twelve dead
 * exports happened in the first place. ADR-0025 records the rule.
 */

const KIB = 1024;
const MIB = 1024 * KIB;

// ── size ladder (SPEC.md §10, docs/ARCHITECTURE.md §1.5, ADR-0010) ───────────────────────

/** At and above this size the analysis goes to a Worker; below it, inline and synchronous. */
export const WORKER_THRESHOLD_BYTES = 256 * KIB;

/**
 * Soft limit. A permanent inline banner appears, heavy editor extensions are switched off,
 * and lint runs on demand rather than on every keystroke. The document still works.
 */
export const SOFT_LIMIT_BYTES = 2 * MIB;

/**
 * Hard limit. Input is REJECTED and THE PREVIOUS DOCUMENT IS KEPT.
 *
 * The legacy build replaced the user's text with `{"error": "File too large..."}` — the
 * worst behaviour of the old implementation, and the reason `documentStore.test.ts` carries
 * a regression test named after it.
 *
 * DERIVED, not re-declared — rule 2 above, and its original worked example. The same
 * threshold has to be enforced inside the engine as well, because a Worker calls
 * `analyzeDocument` directly and never sees this file, so the number lives in
 * `DEFAULT_LINT_OPTIONS.maxBytes` and is named here. Two independent `10 * MIB` literals is
 * exactly the drift this file exists to prevent.
 */
export const HARD_LIMIT_BYTES = DEFAULT_LINT_OPTIONS.maxBytes;

// ── debounce ladder (ADR-0009) ───────────────────────────────────────────────────────────

/**
 * We own lint scheduling; CodeMirror's `linter()` facet is not used, because its own 750 ms
 * trailing debounce would MULTIPLY with ours and its `batchResults` would drop a Worker
 * answer that arrived more than 200 ms after a faster source (ADR-0009).
 */
export const DEBOUNCE_LADDER: ReadonlyArray<{ readonly upToBytes: number; readonly ms: number }> = [
  { upToBytes: WORKER_THRESHOLD_BYTES, ms: 150 },
  { upToBytes: SOFT_LIMIT_BYTES, ms: 400 },
  { upToBytes: HARD_LIMIT_BYTES, ms: 800 },
];

/** Debounce for a document of `bytes`. Above the hard limit, lint only runs on demand. */
export function debounceMsFor(bytes: number): number {
  for (const rung of DEBOUNCE_LADDER) {
    if (bytes < rung.upToBytes) return rung.ms;
  }
  return DEBOUNCE_LADDER[DEBOUNCE_LADDER.length - 1]?.ms ?? 800;
}

// ── persistence and locale, declared where every layer can reach them ────────────────────

/**
 * The complete set of keys this application writes (ADR-0023: the document itself is NEVER
 * stored). Declared in `src/services/types.ts`, beside the `StorageKey` union it generates
 * and the `StorageService` that consumes it, because `services/` and `ui/` both write these
 * keys and neither may import this file.
 *
 * The namespace prefix is shared with the inline theme bootstrap in `index.html`, which reads
 * `jsonfmt.theme` before React mounts. `npm run check:theme` compares that inline script
 * against `scripts/theme-bootstrap.mjs`; nothing compares either of them to the constant, so
 * `theme-bootstrap.mjs` names this file and the reason in a comment of its own.
 */
export { STORAGE_KEYS, DEFAULT_THEME_PREFERENCE, DEFAULT_LOCALE };

// ── engine defaults, as this application configures them ─────────────────────────────────

export const LINT_OPTIONS: LintOptions = DEFAULT_LINT_OPTIONS;

// ── notifications (ADR-0014: at most one toast per action) ───────────────────────────────

export const TOAST_DEFAULT_DURATION_MS = 4000;

/**
 * Repair reports list what changed, so they are given longer. `0` would mean sticky.
 *
 * This used to be declared and never read: no repair notice set `durationMs`, so every one of
 * them fell back to the 4 s default and the comment above was simply false. `runCommand.show`
 * now applies it to any notice whose key names a repair, which is the same `toast.repair.`
 * prefix `App.tsx` keys the persistent banner off.
 */
export const TOAST_REPAIR_DURATION_MS = 8000;

// ── identity ─────────────────────────────────────────────────────────────────────────────

/**
 * The id of the view shown on first load, and the fallback when a registered view id is not
 * found. It is `ViewDescriptor.id` from `features/registry.ts` and nothing else.
 *
 * The product's NAME is deliberately not here. It used to be, as `APP_NAME = 'JSON Formatter
 * & Viewer'`, which nothing imported and which disagreed with the three places the user
 * actually sees it (`index.html` twice and `meta.productName` in the locale catalogue, all
 * three reading "JSON Formatter & Validator"). A user-visible name belongs to the translation
 * catalogue, where a translator can reach it (ADR-0018).
 */
export const DEFAULT_VIEW_ID = 'editor';
